import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, FileText } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import PortalBackground from '../components/PortalBackground';

const CHECKOUT_STORAGE_KEY = 'checkoutPersistedData';

/**
 * DocuSign redirects here after embedded contract signing.
 * On signing_complete, mark contract signed and return to Checkout step 3 (payment).
 */
const ContractSigningReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  const isPortal = window.location.pathname.startsWith('/portal');
  const checkoutPath = isPortal ? '/portal/checkout' : '/start/checkout';

  useEffect(() => {
    const event = searchParams.get('event');
    const envelopeId = searchParams.get('envelopeId');

    const handleReturn = async () => {
      if (!currentUser) {
        setStatus('error');
        setMessage('You must be logged in to complete this step.');
        return;
      }

      if (event === 'signing_complete') {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          let existingProgress = {};
          if (userSnap.exists()) {
            existingProgress = userSnap.data().startFlowProgress || {};
          }

          let checkoutRestore = null;
          try {
            const persisted = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
            if (persisted) checkoutRestore = JSON.parse(persisted);
          } catch (e) {
            console.warn('Could not restore checkout session:', e);
          }

          const contractData = checkoutRestore?.contractData || {
            startDate: checkoutRestore?.contractData?.startDate,
            contractLength: checkoutRestore?.contractData?.contractLength,
          };

          await updateDoc(userRef, {
            startFlowProgress: {
              ...existingProgress,
              contractSigned: true,
              programData: {
                name: checkoutRestore?.selectedProgram?.name || existingProgress.programData?.name,
                startDate: contractData?.startDate || existingProgress.programData?.startDate,
              },
              lastUpdated: new Date().toISOString(),
            },
            pendingContract: {
              envelopeId,
              signingComplete: true,
              clientSignedAt: new Date().toISOString(),
            },
            lastUpdated: new Date().toISOString(),
          });

          // Update lead if we have leadId and envelope record
          if (envelopeId) {
            try {
              const signingSnap = await getDoc(doc(db, 'contract_signings', envelopeId));
              if (signingSnap.exists()) {
                const signingData = signingSnap.data();
                const leadId = signingData.leadId;
                if (leadId) {
                  const leadRef = doc(db, 'accounts', leadId);
                  const leadSnap = await getDoc(leadRef);
                  if (leadSnap.exists()) {
                    const leadData = leadSnap.data();
                    const currentDate = new Date().toISOString().split('T')[0];
                    const substatusDates = leadData.substatusDates || {};
                    substatusDates['Pending Payment'] = currentDate;

                    await updateDoc(leadRef, {
                      contractStatus: 'Client Signed',
                      contractLastUpdated: new Date().toISOString(),
                      substatus: 'Pending Payment',
                      substatusDates,
                      contractData: {
                        startDate: signingData.checkoutContext?.startDate || contractData?.startDate,
                        contractLength: signingData.checkoutContext?.contractLength || contractData?.contractLength,
                      },
                      pendingContractEnvelopeId: envelopeId,
                      lastUpdated: new Date().toISOString(),
                    });
                  }
                }
              }
            } catch (leadErr) {
              console.warn('Could not update lead after contract signing:', leadErr);
            }
          }

          setStatus('complete');
          setMessage('Your contract has been signed. Redirecting you to payment…');

          setTimeout(() => {
            navigate(checkoutPath, {
              state: {
                contractSigned: true,
                contractData,
                selectedProgram: checkoutRestore?.selectedProgram,
                selectedTier: checkoutRestore?.selectedTier,
                billingCycle: checkoutRestore?.billingCycle,
                leadId: checkoutRestore?.leadId,
                customDeliverables: checkoutRestore?.customDeliverables,
                customDeliverableQuantities: checkoutRestore?.customDeliverableQuantities,
              },
            });
          }, 2000);
        } catch (err) {
          console.error('Error updating contract status after signing:', err);
          setStatus('complete');
          setMessage('Your signature was received. Redirecting to checkout…');
          setTimeout(() => navigate(checkoutPath, { state: { contractSigned: true } }), 2000);
        }
      } else if (event === 'decline') {
        setStatus('declined');
        setMessage('You declined to sign the contract. You can return to checkout and try again when ready.');
      } else if (event === 'cancel') {
        setStatus('declined');
        setMessage('You cancelled the signing session. You can return to checkout and try again.');
      } else if (event === 'session_timeout' || event === 'ttl_expired') {
        setStatus('error');
        setMessage('Your signing session expired. Please return to checkout and start again.');
      } else {
        navigate(checkoutPath);
      }
    };

    handleReturn();
  }, [searchParams, currentUser, navigate, checkoutPath]);

  return (
    <div className="relative min-h-screen flex items-center justify-center">
      <PortalBackground />
      <div className="relative max-w-md w-full mx-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="flex justify-center mb-4">
                <Clock className="h-14 w-14 text-blue-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Processing your signature…
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Please wait a moment.</p>
            </>
          )}

          {status === 'complete' && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-14 w-14 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Contract Signed!
              </h2>
              <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === 'declined' && (
            <>
              <div className="flex justify-center mb-4">
                <FileText className="h-14 w-14 text-yellow-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Signing Not Completed
              </h2>
              <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">{message}</p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => navigate(checkoutPath)}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Return to Checkout
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <AlertCircle className="h-14 w-14 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Something Went Wrong
              </h2>
              <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">{message}</p>
              <button
                type="button"
                onClick={() => navigate(checkoutPath)}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Return to Checkout
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractSigningReturn;
